// frontend/src/components/BookTable.js
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Box, TableSortLabel, Typography, Avatar, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { visuallyHidden } from '@mui/utils';

// deletingId als Prop hinzugefügt
function BookTable({ books, sortConfig, requestSort, isMobile, handleEditOpen, handleDelete, handleDetailOpen, columns, deletingId, handleReturnFromList }) {
  const [isReturning, setIsReturning] = useState({});

  const handleReturn = async (loan) => {
    if (!loan?.book?._id) return;
    const bookId = loan.book._id;
    setIsReturning(prev => ({ ...prev, [bookId]: true }));
    try {
      await handleReturnFromList(loan);
    } catch (e) {
      // Fehlerbehandlung ggf. in handleReturnFromList
    } finally {
      setIsReturning(prev => ({ ...prev, [bookId]: false }));
    }
  };

  const handleSort = (property) => {
    requestSort(property); // Logik ist in BookList
  };

  // Platzhalter - Implementiere diese Funktion, um den Inhalt basierend auf der Spalten-ID zu rendern
  const renderTableCellContent = (book, column) => {
    // Verwende locationInfo für den Namen, da populate jetzt in der Aggregation erfolgt
    const value = column.id === 'location' ? book.location?.name : book[column.id];

    if (column.id === 'authors' && Array.isArray(value)) {
      return value.join(', ');
    }
    // Füge hier ggf. weitere Formatierungen hinzu (z.B. Datum)

    // Fallback für einfache Werte oder wenn keine spezielle Formatierung nötig ist
    // Begrenze die Länge der Beschreibung, falls sie angezeigt wird
    if (column.id === 'description' && typeof value === 'string' && value.length > 100) {
        return `${value.substring(0, 100)}...`;
    }

    return value || '-'; // Zeige '-' für leere Werte
  };


  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {/* Thumbnail Spalte */}
            {columns.some(col => col.id === 'thumbnail') && <TableCell>Cover</TableCell>}
            {/* Andere Spalten */}
            {columns.filter(col => col.id !== 'thumbnail').map((column) => (
              <TableCell key={column.id} sortDirection={sortConfig.orderBy === column.id ? sortConfig.order : false}>
                <TableSortLabel
                  active={sortConfig.orderBy === column.id}
                  direction={sortConfig.orderBy === column.id ? sortConfig.order : 'asc'}
                  onClick={() => handleSort(column.id)}
                >
                  {column.label}
                  {sortConfig.orderBy === column.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortConfig.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Verwende direkt 'books' (die bereits sortierte Liste von BookList) */}
          {books.map((book) => {
            // Prüfe, ob dieses Buch gerade gelöscht wird
            const isDeleting = book._id === deletingId;

            // *** NEU: Entscheide, welche URL verwendet wird ***
            // Bevorzuge die gecachte URL, wenn vorhanden.
            // Fallback auf die externe Thumbnail-URL (mit HTTPS-Sicherstellung).
            const coverUrl = book.cachedCoverUrl || book.thumbnail?.replace(/^http:\/\//, 'https://');
            // Prüfe, ob eine "echte" URL vorhanden ist (nicht null und nicht das generische Bild)
            const showRealCover = coverUrl && !coverUrl.endsWith('generic-book-cover.png');

            return (
              <TableRow
                key={book._id}
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    // Zeile leicht ausblenden, wenn gelöscht wird
                    opacity: isDeleting ? 0.5 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                }}
              >
                {/* Thumbnail Zelle */}
                {columns.some(col => col.id === 'thumbnail') && (
                  <TableCell>
                    {/* *** Verwende coverUrl und showRealCover *** */}
                    {showRealCover ? (
                      <Avatar src={coverUrl} alt={book.title} sx={{ width: 50, height: 50 }} variant="rounded" />
                    ) : (
                      // Zeige Platzhalter, wenn kein echtes Cover vorhanden ist
                      <Avatar sx={{ width: 50, height: 50, bgcolor: 'grey.300' }} variant="rounded">
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', textAlign: 'center' }}>Kein Cover</Typography>
                      </Avatar>
                    )}
                  </TableCell>
                )}
                {/* Andere Zellen */}
                {columns.filter(col => col.id !== 'thumbnail').map((column) => (
                  <TableCell key={`${book._id}-${column.id}`}>
                    {/* Rufe die Rendering-Funktion auf */}
                    {renderTableCellContent(book, column)}
                  </TableCell>
                ))}
                {/* Aktionen Zelle */}
                <TableCell align="right">
                  {/* Deaktiviere Buttons, wenn gelöscht wird */}
                  <IconButton aria-label="detail" onClick={() => handleDetailOpen(book)} disabled={isDeleting}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton aria-label="edit" onClick={() => handleEditOpen(book)} disabled={isDeleting}>
                    <EditIcon />
                  </IconButton>
                  <IconButton aria-label="delete" onClick={() => handleDelete(book._id)} disabled={isDeleting}>
                    {/* Zeige Spinner statt Icon, wenn gelöscht wird */}
                    {isDeleting ? <CircularProgress size={24} /> : <DeleteIcon />}
                  </IconButton>
                  {/* Rückgabe-Button für ausgeliehene Bücher, falls handleReturnFromList übergeben */}
                  {typeof book._loan !== 'undefined' && typeof handleReturnFromList === 'function' && (
                    <IconButton aria-label="return" color="success" onClick={() => handleReturn(book._loan)} disabled={isDeleting || isReturning[book._id]}>
                      {isReturning[book._id] ? <CircularProgress size={24} /> : <AssignmentReturnIcon />}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default BookTable;
